'use client';

import { useEffect } from 'react';

function normalizeAnchorId(value: string) {
  return decodeURIComponent(value).split('#').filter(Boolean)[0] ?? '';
}

function getAnchorTargetId(url: URL) {
  if (url.hash.length > 1) {
    return normalizeAnchorId(url.hash.slice(1));
  }

  const segments = url.pathname.split('/').filter(Boolean);
  const lastSegment = segments.at(-1);

  if (!lastSegment || lastSegment.length === 2) {
    return null;
  }

  return normalizeAnchorId(lastSegment);
}

function isHomepageAnchorCandidate(url: URL) {
  const segments = url.pathname.split('/').filter(Boolean);

  if (url.hash.length > 1) {
    return segments.length === 0 || segments.length === 1;
  }

  return segments.length === 1 || (segments.length === 2 && segments[0].length === 2);
}

function scrollToElement(element: HTMLElement, targetId: string) {
  element.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const currentUrl = new URL(window.location.href);
  currentUrl.hash = encodeURIComponent(targetId);

  const nextUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;

  if (currentPath !== nextUrl) {
    window.history.pushState(null, '', nextUrl);
  }
}

export function BodyClientOverride() {
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const link = (event.target as Element | null)?.closest('a[href]');
      if (!(link instanceof HTMLAnchorElement)) return;
      if (link.target && link.target !== '_self') return;

      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (!isHomepageAnchorCandidate(url)) return;

      const targetId = getAnchorTargetId(url);
      if (!targetId) return;

      const target = document.getElementById(targetId);
      if (!target) return;

      event.preventDefault();
      scrollToElement(target, targetId);
    };

    document.addEventListener('click', handleClick, { capture: true });

    return () => {
      document.removeEventListener('click', handleClick, { capture: true });
    };
  }, []);

  return null;
}
